/**
 * AP-39 12.2 — **rechts neben der Liste**: links das Original (PDF und Bild im Browser, Office nur als
 * Download — 15.), rechts, was zum Eintrag gehört.
 *
 * **Nachtrag 1 (Auftraggeber 13.10.2026) — aufgebaut wie die Erfassung der Eingangsrechnung:**
 * - dasselbe Original wie dort (pdf.js, Vergrößerung, Fundstellen markiert — `components/original`);
 * - beide Breiten lassen sich ziehen und bleiben je Benutzer gemerkt: die des Seitenfelds an seinem linken
 *   Rand, die des Originals am Rand dazwischen;
 * - rechts Abschnitte statt einer langen Spalte: die **Positionen des zugeordneten Belegs** mit dem, was
 *   aus dem Dokument gelesen wurde (`WertePositionen`), dann Eingang, Erkannt, Zuordnung, Verlauf.
 *
 * Art und Anmerkung speichern feldweise ohne Knopf (AP-36). „Erkannt" (Phase C): Art mit Quelle, gefundene
 * Belegnummern mit Fundstelle, „Erneut erkennen". Mit `INBOX.AI` (Phase H): Stand des Lesens, was gelesen
 * wurde, „Art mit KI erkennen", „Werte mit KI lesen" und „nicht an die KI".
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Download, EyeOff, Link2, Mail, RefreshCw, RotateCcw, Sparkles, Trash2, Unlink, X } from 'lucide-react'
import { INBOX_KINDS, erfuelltRecht, type InboxValuesView } from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { laufFehlerText } from '@/lib/lauf-fehler'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { downloadFile } from '@/lib/download'
import { fmtDate } from '@/lib/format-date'
import { Abschnitt, FeldZeile, SchalterZeile, feldKlasse, mitStand, useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useTeilung } from '@/lib/teilung'
import { usePosteingang } from '@/components/eingang/posteingang'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { OriginalAnzeige, type OriginalZiel } from '@/components/original/OriginalAnzeige'
import { datumSchreibweisen, type Fundstelle } from '@/components/original/fundstellen'
import {
  assignInboxItem,
  classifyInboxItem,
  deleteInboxItem,
  fetchInboxContent,
  fetchInboxItem,
  fetchInboxOriginal,
  fetchInboxValues,
  inboxItemUrl,
  readInboxItem,
  recognizeInboxItem,
  setInboxIgnored,
  unassignInboxItem,
} from '../api/inbox.api'
import { WertePositionen, werteFundstellen } from './WertePositionen'
import { ZielSuche } from './ZielSuche'
import { ART_TEXT, EREIGNIS_TEXT, KENNZEICHEN_TEXT, STATUS_TEXT, WEG_TEXT, eingangText, groesseText } from '../lib/texte'
import type { InboxFund, InboxItemDetail } from '../types/inbox.types'

const ANZEIGBAR = /^(application\/pdf|image\/(png|jpeg|webp))$/
const gewaehltKlasse = 'outline outline-2 -outline-offset-2 outline-violet-500/70'
const werkzeug = 'inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm hover:bg-muted disabled:opacity-60'

const fundText = (f: InboxFund) =>
  f.quelle === 'SUBJECT' ? 'im Betreff' : f.quelle === 'FILENAME' ? 'im Dateinamen' : f.quelle === 'XML' ? 'in der XML-Datei' : f.quelle === 'AI' ? `von der KI gelesen${f.seite ? ` (Seite ${f.seite})` : ''}` : `auf Seite ${f.seite ?? 1}`

export function EintragSeitenfeld({ id, onWahl, onClose, onGeaendert }: { id: string; onWahl: (id: string) => void; onClose: () => void; onGeaendert: () => void }) {
  const { user, hasModule } = useAuthStore()
  const darf = erfuelltRecht('inbox.item.manage', user?.permissions)
  // Was nicht gebucht ist, wird nicht angeboten: ohne `INBOX.AI` gibt es das Lesen mit KI nicht.
  const mitKi = hasModule('INBOX.AI')
  const zaehlerNeu = usePosteingang((s) => s.zaehlerNeu)
  const [e, setE] = useState<InboxItemDetail | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [erkennt, setErkennt] = useState(false)
  const [werte, setWerte] = useState<InboxValuesView[]>([])
  // Was das Original zeigt: die Fundstelle einer Zeile, sonst ihre Seite.
  const [ziel, setZiel] = useState<OriginalZiel | null>(null)
  // Beide Breiten folgen dem Benutzer: das Seitenfeld als Anteil des Fensters, das Original als Anteil des Seitenfelds.
  const feld = useTeilung({ schluessel: 'split_inbox_panel', label: 'Breite des Seitenfelds', vorgabe: 80, min: 45, max: 98, vonRechts: true, amFenster: true })
  const teilung = useTeilung({ schluessel: 'split_inbox_item', label: 'Breite des Originals', vorgabe: 40, min: 20, max: 75 })

  useEffect(() => {
    let aus = false
    setE(null)
    setFehler(null)
    setWerte([])
    setZiel(null)
    fetchInboxItem(id)
      .then((d) => !aus && setE(d))
      .catch((x) => !aus && setFehler(apiErrorText(x, 'Der Eintrag ließ sich nicht laden.')))
    return () => {
      aus = true
    }
  }, [id])

  // Solange die KI liest, alle zwei Sekunden nachsehen — danach kann der Eintrag erkannt, zugeordnet oder erledigt sein.
  const liest = e?.aiStatus === 'RUNNING'
  useEffect(() => {
    if (!liest) return
    const t = setInterval(() => {
      void fetchInboxItem(id)
        .then((d) => {
          setE(d)
          if (d.aiStatus !== 'RUNNING') {
            onGeaendert()
            void zaehlerNeu()
          }
        })
        .catch(() => undefined)
    }, 2000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, liest])

  // Die Positionen der Ziele mit Werten — neu, sobald sich die Zuordnung ändert oder die KI fertig gelesen hat.
  // Welches Ziel Werte trägt, weiß das Register am Server: Gefragt wird, sobald der Eintrag an irgendeinem Ziel hängt.
  const mitWerten = (e?.assignments ?? []).map((z) => z.targetKind + z.targetId).join(',')
  const gelesenAm = e?.aiReadAt ?? ''
  useEffect(() => {
    if (!mitWerten) {
      setWerte([])
      return
    }
    let aus = false
    fetchInboxValues(id)
      .then((w) => !aus && setWerte(w))
      // Ohne die Positionen bleibt der Eintrag bedienbar — der Beleg ist über die Zuordnung zu erreichen.
      .catch(() => !aus && setWerte([]))
    return () => {
      aus = true
    }
  }, [id, mitWerten, gelesenAm, liest])

  // Vorschau nur für PDF und Bilder; Office-Dateien werden nie eingebettet (15.).
  const anzeigbar = !!e?.hasFile && ANZEIGBAR.test(e.mimeType ?? '')
  const ladeOriginal = useCallback(() => fetchInboxOriginal(id), [id])

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `inboxItem:${id}`,
    bezeichnung: e ? `Posteingang ${e.fileName}` : 'Posteingang',
    daten: e as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const r = await api.patch<{ data: InboxItemDetail }>(inboxItemUrl(id), patch, mitStand(stand))
      setE(r.data.data)
      onGeaendert()
      return r.data.data as unknown as Datensatz
    },
    keepalive: { url: inboxItemUrl(id), method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const art = useFeld<string | null>(stelle, 'kind', { art: 'auswahl', label: 'Art', gesperrt: !darf })
  const notiz = useFeld<string | null>(stelle, 'note', { label: 'Anmerkung', gesperrt: !darf })
  const nichtAnKi = useFeld<boolean>(stelle, 'excludeFromAi', { art: 'schalter', label: 'Nicht an die KI', gesperrt: !darf })

  // Was im Original markiert wird: die Nummern, an denen der Beleg erkannt wurde, was die KI zum Dokument gelesen hat,
  // und je Position des zugeordneten Belegs ihre Zeile im Dokument.
  const klasse = e?.extracted?.klasse
  const fundstellen = useMemo<Fundstelle[]>(() => {
    if (!e) return []
    const bezug = (e.references ?? []).filter((f) => f.quelle === 'PAGE' || f.quelle === 'AI').map((f) => ({ id: `bezug:${f.documentId}`, seite: f.seite ?? null, texte: [f.documentNumber], titel: `Bezug ${f.documentNumber}` }))
    const gelesen = klasse
      ? [
          { id: 'klasse:absender', texte: [klasse.absender], titel: 'Absender' },
          { id: 'klasse:uid', texte: [klasse.uid], titel: 'UID' },
          { id: 'klasse:belegnummer', texte: [klasse.belegnummer], titel: 'Nummer des Dokuments' },
          { id: 'klasse:datum', texte: datumSchreibweisen(klasse.datum), titel: 'Datum des Dokuments' },
        ].filter((f) => f.texte.some(Boolean))
      : []
    return [...bezug, ...gelesen, ...werte.flatMap(werteFundstellen)]
  }, [e, klasse, werte])
  const zeige = useCallback((stelleId: string, seite: number | null) => setZiel((z) => ({ id: stelleId, seite, n: (z?.n ?? 0) + 1 })), [])
  /** Klick auf eine Markierung im Original: Die Zeile dazu rückt ins Bild. */
  const zeigeZeile = (stelleId: string) => {
    setZiel((z) => ({ id: stelleId, seite: null, n: z?.n ?? 0 }))
    Array.from(document.querySelectorAll('[data-fundstelle]'))
      .find((el) => el.getAttribute('data-fundstelle') === stelleId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  const zeile = (stelleId: string) => ({ 'data-fundstelle': stelleId, className: `cursor-pointer border-b border-border last:border-0 ${ziel?.id === stelleId ? gewaehltKlasse : ''}` })

  const herunterladen = async () => {
    if (!e) return
    try {
      const blob = await fetchInboxContent(e.id)
      downloadFile(await blob.arrayBuffer(), e.fileName, blob.type || e.mimeType || 'application/octet-stream')
    } catch (x) {
      setFehler(apiErrorText(x, 'Herunterladen hat nicht geklappt.'))
    }
  }
  const ignorieren = async (an: boolean) => {
    if (!e) return
    try {
      setE(await setInboxIgnored(e.id, an))
      onGeaendert()
      void zaehlerNeu()
    } catch (x) {
      setFehler(apiErrorText(x, 'Das hat nicht geklappt.'))
    }
  }
  const nachZuordnung = (d: InboxItemDetail) => {
    setE(d)
    onGeaendert()
    void zaehlerNeu()
  }
  const loesen = async (assignmentId: string, label: string) => {
    if (!e) return
    if (!(await bestaetige(`Zuordnung zu „${label}" lösen? Ohne weiteres Ziel liegt der Eintrag wieder offen im Posteingang.`, { ok: 'Lösen' }))) return
    try {
      nachZuordnung(await unassignInboxItem(e.id, assignmentId))
    } catch (x) {
      setFehler(apiErrorText(x, 'Lösen hat nicht geklappt.'))
    }
  }

  const erneutErkennen = async () => {
    if (!e) return
    setErkennt(true)
    try {
      nachZuordnung(await recognizeInboxItem(e.id))
    } catch (x) {
      setFehler(apiErrorText(x, 'Erkennen hat nicht geklappt.'))
    } finally {
      setErkennt(false)
    }
  }

  const mitKiLesen = async (was: 'art' | { targetKind: string; targetId: string }) => {
    if (!e) return
    setFehler(null)
    try {
      setE(was === 'art' ? await classifyInboxItem(e.id) : await readInboxItem(e.id, was))
    } catch (x) {
      setFehler(apiErrorText(x, 'Das Lesen mit KI ließ sich nicht starten.'))
    }
  }

  /** Nach „Übernehmen" oder „Verwerfen": Der Eintrag kann dadurch erledigt sein — Status, Verlauf und Liste nachziehen. */
  const nachWerten = (w: InboxValuesView[]) => {
    setWerte(w)
    void fetchInboxItem(id)
      .then(nachZuordnung)
      .catch(() => undefined)
  }

  const loeschen = async () => {
    if (!e) return
    const gruppe = e.isMailText && e.group.length > 0
    const text = gruppe
      ? `Die E-Mail „${e.subject ?? e.fileName}" mit ${e.group.length} ${e.group.length === 1 ? 'Datei' : 'Dateien'} in den Papierkorb legen? Sie bleibt dort 30 Tage.`
      : `„${e.fileName}" in den Papierkorb legen? Der Eintrag bleibt dort 30 Tage.`
    if (!(await bestaetige(text, { ok: 'In den Papierkorb', gefaehrlich: true }))) return
    try {
      await deleteInboxItem(e.id)
      onGeaendert()
      void zaehlerNeu()
      onClose()
    } catch (x) {
      setFehler(apiErrorText(x, 'Löschen hat nicht geklappt.'))
    }
  }

  const kiMoeglich = !!e && mitKi && e.hasFile && !e.isMailText && !e.deletedAt && e.status !== 'FAILED'
  const kiDarf = kiMoeglich && darf && !e.excludeFromAi
  const offeneVorschlaege = e ? (e.suggestions ?? []).filter((v) => !e.assignments.some((z) => z.targetKind === v.targetKind && z.targetId === v.targetId)) : []
  const erkanntes = !!e && !!(e.references?.length || e.kindSource || klasse)

  return (
    <aside
      className={`fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-border bg-card shadow-xl lg:w-[var(--pe-breite)] ${feld.zieht || teilung.zieht ? 'select-none' : ''}`}
      style={{ '--pe-breite': `${feld.anteil}vw` } as CSSProperties}
      aria-label="Eintrag im Posteingang"
    >
      {/* Der linke Rand des Seitenfelds lässt sich ziehen — so breit, wie Original und Positionen es brauchen. */}
      <div {...feld.griff} className="group absolute inset-y-0 left-0 z-10 hidden w-3 -translate-x-1/2 cursor-col-resize touch-none justify-center focus:outline-none lg:flex">
        <span className={`h-full w-1 transition-colors group-hover:bg-primary/60 group-focus-visible:bg-primary ${feld.zieht ? 'bg-primary' : 'bg-transparent'}`} />
      </div>

      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold text-foreground" title={e?.fileName}>
            {e?.fileName ?? '…'}
          </div>
          {e && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
              <span className={`inline-flex rounded-full px-2 py-0.5 font-medium ${STATUS_TEXT[e.status].klasse}`}>{STATUS_TEXT[e.status].label}</span>
              {e.kind && <span className="inline-flex rounded-full border border-border px-2 py-0.5 font-medium">{ART_TEXT[e.kind]}</span>}
              {e.flags.map((f) => (
                <span key={f} className="inline-flex rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                  {KENNZEICHEN_TEXT[f] ?? f}
                </span>
              ))}
              {e.partnerName && <span className="text-muted-foreground">· {e.partnerName}</span>}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {e?.hasFile && (
            <button type="button" onClick={() => void herunterladen()} className={werkzeug}>
              <Download className="h-4 w-4" /> Herunterladen
            </button>
          )}
          {e && darf && !e.deletedAt && e.status !== 'DUPLICATE' && (
            e.status === 'IGNORED' ? (
              <button type="button" onClick={() => void ignorieren(false)} className={werkzeug}>
                <RotateCcw className="h-4 w-4" /> Nicht mehr ignorieren
              </button>
            ) : (
              <button type="button" onClick={() => void ignorieren(true)} className={werkzeug}>
                <EyeOff className="h-4 w-4" /> Ignorieren
              </button>
            )
          )}
          {e && darf && !e.deletedAt && (
            <button type="button" onClick={() => void loeschen()} className={`${werkzeug} text-destructive hover:bg-destructive/10`}>
              <Trash2 className="h-4 w-4" /> Löschen
            </button>
          )}
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Nebeneinander ab `lg`; der Rand dazwischen lässt sich ziehen. Darunter stehen Original und Abschnitte untereinander. */}
      <div
        ref={teilung.behaelter}
        className="grid min-h-0 flex-1 gap-3 overflow-y-auto p-3 lg:grid-cols-[minmax(0,var(--pe-links))_0.75rem_minmax(0,var(--pe-rechts))] lg:gap-0 lg:overflow-hidden"
        style={{ '--pe-links': `${teilung.anteil}fr`, '--pe-rechts': `${100 - teilung.anteil}fr` } as CSSProperties}
      >
        <div className="h-[70vh] min-h-0 lg:h-full">
          {anzeigbar ? (
            <OriginalAnzeige schluessel={id} lade={ladeOriginal} vorhanden ziel={ziel} fundstellen={fundstellen} onFundstelle={zeigeZeile} titel="Original" />
          ) : (
            <div className="flex h-full min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-border px-6 text-center text-sm text-muted-foreground">
              {!e ? 'Laden…' : e.hasFile ? 'Für diese Datei gibt es keine Vorschau — herunterladen und im passenden Programm öffnen.' : (e.error ?? 'Zu diesem Eintrag gibt es keine Datei.')}
            </div>
          )}
        </div>
        <div {...teilung.griff} className="group hidden cursor-col-resize touch-none justify-center focus:outline-none lg:flex">
          <span className={`h-full w-1 rounded-full transition-colors group-hover:bg-primary/60 group-focus-visible:bg-primary ${teilung.zieht ? 'bg-primary' : 'bg-transparent'}`} />
        </div>

        <div className="min-w-0 space-y-3 lg:overflow-y-auto lg:pr-1">
          {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}

          {e && (
            <>
              {/* Zuerst das, woran gearbeitet wird: die Positionen des zugeordneten Belegs mit dem, was gelesen wurde. */}
              {werte.map((a) => (
                <WertePositionen
                  key={a.targetKind + a.targetId}
                  itemId={e.id}
                  ansicht={a}
                  darf={darf && !e.deletedAt}
                  lesen={kiDarf ? () => void mitKiLesen({ targetKind: a.targetKind, targetId: a.targetId }) : undefined}
                  liest={liest}
                  gewaehlt={ziel?.id ?? null}
                  onZeige={zeige}
                  onAnsichten={nachWerten}
                  onFehler={setFehler}
                />
              ))}

              <Abschnitt titel="Eingang">
                {/* Zwei Gruppen nebeneinander, solange das Seitenfeld breit genug ist — sonst untereinander. */}
                <div className="grid gap-x-8 gap-y-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(17rem, 1fr))' }}>
                  <Gruppe titel="Dokument">
                    <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
                      <dt className="text-muted-foreground">Eingang</dt>
                      <dd>
                        {eingangText(e.receivedAt)} · {WEG_TEXT[e.source]}
                      </dd>
                      {(e.senderAddress || e.senderName) && (
                        <>
                          <dt className="text-muted-foreground">Absender</dt>
                          <dd className="break-words">{[e.senderName, e.senderAddress && `<${e.senderAddress}>`].filter(Boolean).join(' ')}</dd>
                        </>
                      )}
                      {e.subject && (
                        <>
                          <dt className="text-muted-foreground">Betreff</dt>
                          <dd className="break-words">{e.subject}</dd>
                        </>
                      )}
                      {e.partnerName && (
                        <>
                          <dt className="text-muted-foreground">Partner</dt>
                          <dd className="font-medium">{e.partnerName}</dd>
                        </>
                      )}
                      {e.responsibleName && (
                        <>
                          <dt className="text-muted-foreground">Zuständig</dt>
                          <dd>{e.responsibleName}</dd>
                        </>
                      )}
                      {e.sizeBytes ? (
                        <>
                          <dt className="text-muted-foreground">Größe</dt>
                          <dd>{groesseText(e.sizeBytes)}</dd>
                        </>
                      ) : null}
                      {e.status === 'FAILED' && e.error && (
                        <>
                          <dt className="text-muted-foreground">Grund</dt>
                          <dd className="text-destructive">{e.error}</dd>
                        </>
                      )}
                      {e.duplicateOfId && (
                        <>
                          <dt className="text-muted-foreground">Dublette von</dt>
                          <dd>
                            <button type="button" className="text-primary hover:underline" onClick={() => onWahl(e.duplicateOfId!)}>
                              dem ersten Eingang öffnen
                            </button>
                          </dd>
                        </>
                      )}
                    </dl>
                  </Gruppe>
                  <Gruppe titel="Einordnung">
                    <FeldZeile label="Art" status={art}>
                      <select className={`${feldKlasse} w-full font-semibold`} {...art.props}>
                        <option value="">— noch nicht bestimmt —</option>
                        {INBOX_KINDS.map((k) => (
                          <option key={k} value={k}>
                            {ART_TEXT[k]}
                          </option>
                        ))}
                      </select>
                    </FeldZeile>
                    <FeldZeile label="Anmerkung" status={notiz}>
                      <textarea rows={2} className={`${feldKlasse} w-full`} {...notiz.props} />
                    </FeldZeile>
                  </Gruppe>
                </div>
              </Abschnitt>

              {(erkanntes || kiMoeglich) && (
                <Abschnitt
                  titel="Erkannt"
                  aktionen={
                    <span className="flex flex-wrap items-center justify-end gap-2">
                      {kiDarf && e.assignments.length === 0 && e.kindSource !== 'MANUAL' && (
                        <button type="button" disabled={liest} onClick={() => void mitKiLesen('art')} className={werkzeug}>
                          <Sparkles className="h-4 w-4" /> Art mit KI erkennen
                        </button>
                      )}
                      {darf && !e.deletedAt && e.status !== 'FAILED' && e.status !== 'DUPLICATE' && e.status !== 'IGNORED' && (
                        <button type="button" disabled={erkennt} onClick={() => void erneutErkennen()} className={werkzeug}>
                          <RefreshCw className={`h-4 w-4 ${erkennt ? 'animate-spin' : ''}`} /> Erneut erkennen
                        </button>
                      )}
                    </span>
                  }
                >
                  {liest && werte.length === 0 && (
                    <p className="text-sm">
                      <KiArbeitet text="Die KI liest" className="font-medium text-violet-700 dark:text-violet-300" />
                    </p>
                  )}
                  {e.aiStatus === 'FAILED' && <p className="text-sm text-destructive">Lesen mit KI fehlgeschlagen: {laufFehlerText(e.aiError)}</p>}
                  {erkanntes && (
                    <div className="overflow-x-auto rounded-md border border-border bg-background">
                      <table className="w-full text-sm">
                        <thead className="border-b border-border bg-muted/50 text-left">
                          <tr>
                            <th className="px-3 py-1.5 font-medium">Feld</th>
                            <th className="px-3 py-1.5 font-medium">erkannt</th>
                            <th className="px-3 py-1.5 font-medium">woher</th>
                          </tr>
                        </thead>
                        <tbody>
                          {e.kind && (
                            <tr className="border-b border-border last:border-0">
                              <td className="whitespace-nowrap px-3 py-1.5">Art</td>
                              <td className="px-3 py-1.5 font-medium">{ART_TEXT[e.kind]}</td>
                              <td className="px-3 py-1.5 text-muted-foreground">
                                {e.kindSource === 'MANUAL' ? 'von Hand' : e.kindSource === 'AI' ? 'von der KI gelesen' : 'nach Regeln'}
                                {e.confidence ? ` · Sicherheit ${e.confidence}` : ''}
                              </td>
                            </tr>
                          )}
                          {(
                            [
                              ['klasse:absender', 'Absender', klasse?.absender],
                              ['klasse:uid', 'UID', klasse?.uid],
                              ['klasse:belegnummer', 'Nummer des Dokuments', klasse?.belegnummer],
                              ['klasse:datum', 'Datum des Dokuments', klasse?.datum ? fmtDate(klasse.datum) : null],
                            ] as const
                          )
                            .filter(([, , wert]) => !!wert)
                            .map(([stelleId, label, wert]) => (
                              <tr key={stelleId} {...zeile(stelleId)} onClick={() => zeige(stelleId, null)}>
                                <td className="whitespace-nowrap px-3 py-1.5">{label}</td>
                                <td className="px-3 py-1.5 font-medium">{wert}</td>
                                <td className="px-3 py-1.5 text-muted-foreground">von der KI gelesen</td>
                              </tr>
                            ))}
                          {(e.references ?? []).map((f) => (
                            <tr key={f.documentId} {...zeile(`bezug:${f.documentId}`)} onClick={() => zeige(`bezug:${f.documentId}`, f.seite ?? null)}>
                              <td className="whitespace-nowrap px-3 py-1.5">Bezug</td>
                              <td className="px-3 py-1.5 font-medium">{f.documentNumber}</td>
                              <td className="px-3 py-1.5 text-muted-foreground">
                                {fundText(f)}
                                {f.quelle === 'PAGE' && f.ausschnitt && <span className="block truncate text-xs italic" title={f.ausschnitt}>{f.ausschnitt}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!erkanntes && !liest && <p className="text-sm text-muted-foreground">Die Regeln haben in diesem Dokument nichts erkannt.</p>}
                  {kiMoeglich && <SchalterZeile feld={nichtAnKi} label="Nicht an die KI" hinweis="Diese Datei wird nie an den KI-Anbieter gesendet." />}
                </Abschnitt>
              )}

              {(e.assignments.length > 0 || offeneVorschlaege.length > 0 || (darf && e.hasFile && !e.deletedAt && e.status !== 'FAILED')) && (
                <Abschnitt titel="Zuordnung">
                  {e.assignments.length > 0 && (
                    <ul className="space-y-1 text-sm">
                      {e.assignments.map((z) => (
                        <li key={z.id} className="flex items-center justify-between gap-2 rounded-md bg-emerald-500/10 px-2 py-1">
                          <span className="flex min-w-0 items-center gap-1.5">
                            <Link2 className="h-3.5 w-3.5 shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden />
                            {z.url ? (
                              <Link to={z.url} className="truncate font-medium text-primary hover:underline">
                                {z.targetLabel}
                              </Link>
                            ) : (
                              <span className="truncate font-medium">{z.targetLabel}</span>
                            )}
                            {z.auto && <span className="shrink-0 text-xs text-muted-foreground">automatisch</span>}
                          </span>
                          {darf && (
                            <button type="button" onClick={() => void loesen(z.id, z.targetLabel)} className="inline-flex flex-shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
                              <Unlink className="h-3.5 w-3.5" /> Lösen
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {offeneVorschlaege.length > 0 && (
                    <div>
                      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vorschläge</h4>
                      <ul className="space-y-1">
                        {offeneVorschlaege.map((v) => (
                          <li key={v.targetKind + v.targetId} className="flex items-center justify-between gap-2 rounded-md bg-sky-500/10 px-2 py-1">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium">{v.label}</div>
                              <div className="truncate text-xs text-muted-foreground">{v.grund}</div>
                            </div>
                            {darf && e.hasFile && (
                              <button
                                type="button"
                                onClick={() => void assignInboxItem(e.id, v.targetKind, v.targetId).then(nachZuordnung).catch((x) => setFehler(apiErrorText(x, 'Zuordnen hat nicht geklappt.')))}
                                className="inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-xs hover:bg-card"
                              >
                                <Link2 className="h-3.5 w-3.5" /> Zuordnen
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {darf && e.hasFile && !e.deletedAt && e.status !== 'FAILED' && (
                    <div>
                      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{e.assignments.length || offeneVorschlaege.length ? 'Anderes Ziel suchen' : 'Ziel suchen'}</h4>
                      <ZielSuche itemId={e.id} besetzt={e.assignments.map((z) => z.targetKind + ':' + z.targetId)} onWahl={async (z) => nachZuordnung(await assignInboxItem(e.id, z.targetKind, z.targetId))} />
                    </div>
                  )}
                </Abschnitt>
              )}

              {e.group.length > 0 && (
                <Abschnitt
                  titel={
                    <span className="flex items-center gap-1.5">
                      <Mail className="h-4 w-4" aria-hidden /> Aus derselben E-Mail
                    </span>
                  }
                >
                  <ul className="space-y-1 text-sm">
                    {e.group.map((g) => (
                      <li key={g.id}>
                        <button type="button" className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1 text-left hover:bg-muted" onClick={() => onWahl(g.id)}>
                          <span className="truncate">{g.fileName}</span>
                          <span className={`inline-flex flex-shrink-0 rounded-full px-2 py-0.5 text-xs ${STATUS_TEXT[g.status].klasse}`}>{STATUS_TEXT[g.status].label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Abschnitt>
              )}

              <Abschnitt titel="Verlauf">
                <ol className="space-y-0.5 text-xs text-muted-foreground">
                  {e.events.map((v) => (
                    <li key={v.id}>
                      {eingangText(v.createdAt)} · {EREIGNIS_TEXT[v.type] ?? v.type}
                      {typeof v.detail?.label === 'string' && ` — ${v.detail.label}`}
                      {v.detail?.auto === true && ' (automatisch)'}
                      {typeof v.detail?.hinweis === 'string' && ` · ${v.detail.hinweis}`}
                    </li>
                  ))}
                </ol>
              </Abschnitt>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}

/** Eine Gruppe zusammengehöriger Angaben — Überschrift mit Linie, darunter untereinander (wie im Kopf der Eingangsrechnung). */
function Gruppe({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={titel} className="min-w-0">
      <h4 className="mb-2 border-b border-border pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titel}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  )
}
